
import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';

interface InstanceSettings {
  maxAdminAccounts: number;
  maxUserAccounts: number;
  allowCustomDomain: boolean;
  domains: string[];
  primaryDomain: string;
}

export const useInstanceSettings = (instanceId: string) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState<InstanceSettings>({
    maxAdminAccounts: 3,
    maxUserAccounts: 100,
    allowCustomDomain: false,
    domains: [],
    primaryDomain: '',
  });
  const [isSaving, setIsSaving] = useState(false);

  // Fetch instance settings
  const fetchSettings = async () => {
    setIsLoading(true);
    try {
      // In a real implementation, this would fetch from an API
      // For now, we're using empty default values since we're removing mock data
      
      // Example API call for future implementation:
      // const response = await fetch(`/api/instances/${instanceId}/settings`);
      // const data = await response.json();
      // setSettings(data);
      
      setSettings({
        maxAdminAccounts: 3,
        maxUserAccounts: 100,
        allowCustomDomain: false,
        domains: [],
        primaryDomain: '',
      });
    } catch (error) {
      console.error('Error fetching instance settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load instance settings',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateSettings = async (newSettings: InstanceSettings) => {
    setIsSaving(true);
    try {
      // In a real implementation, this would call an API
      // Example API call for future implementation:
      // await fetch(`/api/instances/${instanceId}/settings`, {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(newSettings)
      // });
      
      setSettings(newSettings);
      toast({
        title: 'Settings Saved',
        description: 'Instance settings have been updated successfully',
      });
      return true;
    } catch (error) {
      console.error('Error updating instance settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to update instance settings',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const addDomain = async (domain: string) => {
    if (settings.domains.includes(domain)) {
      toast({
        title: 'Domain Exists',
        description: 'This domain is already added to the instance',
        variant: 'destructive',
      });
      return false;
    }

    const newSettings = {
      ...settings,
      domains: [...settings.domains, domain],
    };
    
    return await updateSettings(newSettings);
  };

  const removeDomain = async (domain: string) => {
    if (domain === settings.primaryDomain) {
      toast({
        title: 'Cannot Remove',
        description: 'Cannot remove the primary domain',
        variant: 'destructive',
      });
      return false;
    }

    const newSettings = {
      ...settings,
      domains: settings.domains.filter(d => d !== domain),
    };
    
    return await updateSettings(newSettings);
  };

  const setPrimaryDomain = async (domain: string) => {
    if (!settings.domains.includes(domain)) {
      toast({
        title: 'Invalid Domain',
        description: 'This domain is not associated with the instance',
        variant: 'destructive',
      });
      return false;
    }

    const newSettings = {
      ...settings,
      primaryDomain: domain,
    };
    
    return await updateSettings(newSettings);
  };

  const updateLimits = async (maxAdmins: number, maxUsers: number) => {
    const newSettings = {
      ...settings,
      maxAdminAccounts: maxAdmins,
      maxUserAccounts: maxUsers,
    };
    
    return await updateSettings(newSettings);
  };

  const toggleCustomDomain = async (allow: boolean) => {
    const newSettings = {
      ...settings,
      allowCustomDomain: allow,
    };
    
    return await updateSettings(newSettings);
  };

  return {
    settings,
    isLoading,
    isSaving,
    fetchSettings,
    updateSettings,
    addDomain,
    removeDomain,
    setPrimaryDomain,
    updateLimits,
    toggleCustomDomain,
  };
};
